import { Box, Checkbox, FormHelperText, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectBasketId, selectExitStrategy, selectStrategyBuilderMode, selectTradeOrders } from '../../../selectors/strategyBuilder'
import { Broker_OrderType, OrderStatus, OrderType } from '../../../proto/generated/order'
import { updateExitStrategy } from '../../../reducer/strategyBuilder.reducer'
import { selectOrdersHistory } from '../../../selectors/orders'
import { useStyles } from './styles'
import { ExtraSmallButton } from '../../custom'
import ClearIcon from '../../../assets/icons/ClearIcon'
import { ModifyType } from '../../ordersWidget/OrderRow'
import { AnalyzeGroupedPosition, GroupedPosition } from '../../../reducer/positions.reducer'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { securityKeysFromRefs } from '../../../hooks/useSecurityMarketTicks'
import { useSlTargetLiveValidation } from './useSlTargetLiveValidation'

export type SlTargetType = 'points' | 'percentage' | 'exact'

export const orderTypes = [
    {
        value: Broker_OrderType.LIMIT_ORDER,
        label: 'Limit Order'
    },
    {
        value: Broker_OrderType.MARKET_ORDER,
        label: 'Market Order'
    }
]

const SlAndTargetInputs: React.FC<{
    disabled: boolean, disabledCheckbox?: boolean;
    disableStopLoss?: boolean;
    disableTarget?: boolean;
    initialStopLoss?: number
    initialTarget?: number
    modifyType?: ModifyType
    basketPosition?: GroupedPosition | AnalyzeGroupedPosition
    strategyMode?: 'trade' | 'analyze' | 'squareOff'
    position?: GroupedPosition | AnalyzeGroupedPosition
    isEditingExistingSL?: boolean
    isEditingExistingTarget?: boolean
    onValidationChange?: (isValid: boolean) => void,
    showSLTargetInfo?: boolean
}> = ({ disabled, disabledCheckbox, disableStopLoss, disableTarget, initialStopLoss, initialTarget, basketPosition, strategyMode, position, isEditingExistingSL, isEditingExistingTarget, onValidationChange, showSLTargetInfo = false, modifyType }) => {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { addStopLoss, addTarget, slValue, targetValue } = useSelector(selectExitStrategy);
    const orderHistory = useSelector(selectOrdersHistory)
    const tradeOrders = useSelector(selectTradeOrders)
    const stopLossInputRef = useRef<HTMLInputElement>(null)
    const targetInputRef = useRef<HTMLInputElement>(null)
    const initializedRef = useRef(false);
    const effectivePosition = position || basketPosition;
    const isSingleSecurity = useMemo(() => {
        return (effectivePosition?.children?.length ?? 0 ) === 1 && !!effectivePosition?.groupId?.startsWith('default_')
    }, [effectivePosition?.children, effectivePosition?.groupId])
    const mode = useSelector(selectStrategyBuilderMode)

    const [selectedSecurity, setSelectedSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const positionKey = effectivePosition ? getSecurityKey(effectivePosition?.security?.exchange, effectivePosition?.security?.code) : ''
    useEffect(() => {
        if (!positionKey) { setSelectedSecurity(undefined); return }
        if (isSingleSecurity) {
            getSecurityAsync(positionKey).then(setSelectedSecurity)
        } else {
            getSecurityAsync(positionKey).then((posSec) => {
                const underlyingCode = posSec?.underlyingSecurityCode
                if (underlyingCode) getSecurityAsync(getSecurityKey(effectivePosition!.security!.exchange, underlyingCode)).then(setSelectedSecurity)
                else setSelectedSecurity(posSec)
            })
        }
    }, [isSingleSecurity, positionKey, effectivePosition])

    // State for points/percentage toggle
    // For editing existing orders in single securities, we use 'exact' mode
    const [slValueType, setSlValueType] = useState<SlTargetType>(isEditingExistingSL && isSingleSecurity ? 'exact' : 'points')
    const [targetValueType, setTargetValueType] = useState<SlTargetType>(isEditingExistingTarget && isSingleSecurity ? 'exact' : 'points')

    // Determine if position is long or short
    const isLongPosition = useMemo(() => {
        if (!effectivePosition) return true

        const effectiveQuantity = effectivePosition.quantity !== 0
            ? effectivePosition.quantity
            : (() => {
                const children = effectivePosition.children || []
                const totalPremium = children.reduce((sum, child) => sum + (child.avgPrice * child.quantity), 0)
                return totalPremium > 0 ? Math.abs(totalPremium) : -Math.abs(totalPremium)
            })()

        return effectiveQuantity > 0
    }, [effectivePosition])

    const singleSecurityQuantity = useMemo(() => {
        if (!effectivePosition) return 1
        if (effectivePosition.quantity !== 0) return Math.abs(effectivePosition.quantity)
        return Math.abs(selectedSecurity?.lotSize ?? 1)
    }, [effectivePosition, selectedSecurity?.lotSize])

    const incomingPositions = useMemo(() => {
        if (!effectivePosition) return []
        const analyzePosition = effectivePosition as AnalyzeGroupedPosition
        return (analyzePosition.children || []).filter(childPosition => childPosition.new || childPosition.addedQuantity)
    }, [effectivePosition])

    const snapshotKeys = useMemo(() => {
        const refs: { exchange: number; code: string }[] = []
        if (effectivePosition?.security?.code) refs.push(effectivePosition.security)
        const analyzePosition = effectivePosition as AnalyzeGroupedPosition | undefined
        analyzePosition?.children?.forEach((c) => refs.push(c.security))
        incomingPositions.forEach((c) => refs.push(c.security))
        return securityKeysFromRefs(refs)
    }, [effectivePosition, incomingPositions])
    const live = useSlTargetLiveValidation(
        {
            effectivePosition,
            snapshots: {},
            tradeOrders,
            selectedSecurity,
            mode,
            incomingPositions,
            isSingleSecurity,
            isLongPosition,
            singleSecurityQuantity,
            positionKey,
            addStopLoss,
            addTarget,
            slValue,
            targetValue,
            slValueType,
            targetValueType,
            showSLTargetInfo,
            effectivePositionAvgPrice: effectivePosition?.avgPrice,
        },
        snapshotKeys
    )

    const {
        isSlValid,
        isTargetValid,
        slInfoText,
        targetInfoText,
        slHelperText,
        targetHelperText,
    } = live

    const basketOpenSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [effectivePosition?.groupId, orderHistory])

    const basketInactiveSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                o.status === OrderStatus.INACTIVE && o.orderType === OrderType.STOP_LOSS
        })
    }, [effectivePosition?.groupId, orderHistory]);
    const basketOpenTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [effectivePosition?.groupId, orderHistory])

    const basketInactiveTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === effectivePosition?.groupId &&
            o.status === OrderStatus.INACTIVE && o.orderType === OrderType.TARGET
        })
    }, [effectivePosition?.groupId, orderHistory])


    // Overall validation: check if both SL and Target are valid
    const isValid = useMemo(() => {
        // If SL is enabled and invalid, return false
        if (addStopLoss && !isSlValid) return false

        // If Target is enabled and invalid, return false
        if (addTarget && !isTargetValid) return false

        // Otherwise, validation passes
        return true
    }, [addStopLoss, isSlValid, addTarget, isTargetValid])

    // Notify parent component when validation changes
    useEffect(() => {
        onValidationChange?.(isValid)
    }, [isValid, onValidationChange])

    useEffect(() => {
        if (initializedRef.current) return;

        if (initialStopLoss !== undefined) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: initialStopLoss
            }))
        }

        if (initialTarget !== undefined) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: initialTarget
            }))
        }

        initializedRef.current = true;
    }, [initialStopLoss, initialTarget]);



    useEffect(() => {
        if (!initializedRef.current) return;   // ⬅️ IMPORTANT
        if (initialStopLoss !== undefined) return;

        if (basketOpenSlOrder) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: (basketOpenSlOrder.triggerPrice ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else if (basketInactiveSlOrder) {
            dispatch(updateExitStrategy({
                addStopLoss: true,
                slValue: (basketInactiveSlOrder.triggerPrice ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else {
            dispatch(updateExitStrategy({
                addStopLoss: false,
                slValue: ''
            }))
        }
    }, [basketOpenSlOrder, basketInactiveSlOrder, selectedSecurity]);

    useEffect(() => {
        if (!initializedRef.current) return;
        if (initialTarget !== undefined) return

        if (basketOpenTargetOrder) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: (basketOpenTargetOrder.price ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else if (basketInactiveTargetOrder) {
            dispatch(updateExitStrategy({
                addTarget: true,
                targetValue: (basketInactiveTargetOrder.price ?? 0) /
                    (selectedSecurity?.priceMultiplier ?? 100)
            }))
        } else {
            dispatch(updateExitStrategy({
                addTarget: false,
                targetValue: ''
            }))
        }
    }, [
        basketOpenTargetOrder,
        basketInactiveTargetOrder,
        selectedSecurity,
        initialTarget
    ])

    const toggleAddStopLoss = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateExitStrategy({
            addStopLoss: event.target.checked
        }))
        setTimeout(() => {
            stopLossInputRef.current?.focus()
        }, 100)
    }, []);

    const onStopLossChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value === '' ? '' : Number(event.target.value)

        dispatch(updateExitStrategy({
            slValue: inputValue
        }))
    }, [dispatch])

    const onSlValueTypeChange = useCallback((_event: React.MouseEvent<HTMLElement>, newValueType: SlTargetType) => {
        if (newValueType !== null) {
            setSlValueType(newValueType)
        }
    }, [])

    const toggleAddTarget = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateExitStrategy({
            addTarget: event.target.checked
        }))
        setTimeout(() => {
            targetInputRef.current?.focus()
        }, 100)
    }, []);

    const onTargetChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value === '' ? '' : Number(event.target.value)

        dispatch(updateExitStrategy({
            targetValue: inputValue
        }))
    }, [dispatch])

    const onTargetValueTypeChange = useCallback((_event: React.MouseEvent<HTMLElement>, newValueType: SlTargetType) => {
        if (newValueType !== null) {
            setTargetValueType(newValueType)
        }
    }, [])

    const onStopLossBlur = useCallback(() => {
        setSlInputFocused(false)
        if (slValue === '') {
            dispatch(updateExitStrategy({
                addStopLoss: false,
                slValue: ''
            }))
        }
    }, [dispatch, slValue])

    const onTargetBlur = useCallback(() => {
        setTargetInputFocused(false)
        if (targetValue === '') {
            dispatch(updateExitStrategy({
                addTarget: false,
                targetValue: ''
            }))
        }
    }, [dispatch, targetValue])

    const preventInputBlur = useCallback((event: React.MouseEvent) => {
        event.preventDefault()
    }, [])

    const [targetInputFocused, setTargetInputFocused] = useState(false)
    const [slInputFocused, setSlInputFocused] = useState(false)
    const [targetHovered, setTargetHovered] = useState(false)
    const [slHovered, setSlHovered] = useState(false)

    const targetField = useMemo(() => {
        const showTargetTooltip = !isTargetValid && (targetInputFocused || targetHovered)
        
        return <>
            {!disabledCheckbox && <Stack 
                direction="row" 
                alignItems="center" 
                sx={{ marginBottom: 0.5, justifyContent: 'space-between' }}
                onMouseEnter={() => setTargetHovered(true)}
                onMouseLeave={() => setTargetHovered(false)}
            >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Checkbox checked={addTarget} onChange={toggleAddTarget} size='small' sx={{ padding: 0, marginRight: 0.5 }} disabled={disabled} />
                    <Typography variant='body1' className={classes.slTargetLabel}>TARGET</Typography>
                </Box>
            </Stack>}
            <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={() => setTargetHovered(true)}
                onMouseLeave={() => setTargetHovered(false)}
            >
                {showTargetTooltip && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.75rem',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            marginBottom: '4px',
                        }}
                    >
                        {targetInfoText}
                    </Box>
                )}
                <TextField
                    InputLabelProps={{
                        shrink: true
                    }}
                    type="number"
                    size="small"
                    fullWidth
                    value={targetValue}
                    disabled={!addTarget || disabled}
                    onChange={onTargetChange}
                    autoComplete='off'
                    inputRef={targetInputRef}
                    error={!isTargetValid && addTarget}
                    onFocus={() => setTargetInputFocused(true)}
                    onBlur={onTargetBlur}
                    inputProps={{
                        step: 1
                    }}
                    InputProps={{
                        sx: {

                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: theme => theme.palette.text.disabled,
                            },

                            '& input[type=number]': {
                                MozAppearance: 'textfield'
                            },
                            paddingRight: 0.5,
                        },
                        endAdornment: (
                            <>
                            {!addTarget && !disableTarget && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddTarget(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={isSingleSecurity ? targetValueType : 'mtm'}
                                aria-label="text alignment"
                                onMouseDown={preventInputBlur}
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                {targetValue !== '' && (
                                    <ExtraSmallButton
                                        sx={{
                                            minWidth: 28,
                                            height: '28px !important',
                                            p: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        onClick={() => {
                                            dispatch(updateExitStrategy({
                                                addTarget: false,
                                                targetValue: ''
                                            }))
                                        }}
                                    >
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </ExtraSmallButton>
                                )}
                                {isSingleSecurity ? (
                                    targetValueType !== 'exact' && (
                                        <Box component="span" sx={{ display: 'contents' }}>
                                            <ToggleButton
                                                value="points"
                                                onClick={onTargetValueTypeChange}
                                                sx={{ color: (theme) => targetValueType === 'points' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                Pts
                                            </ToggleButton>
                                            <ToggleButton
                                                value="percentage"
                                                onClick={onTargetValueTypeChange}
                                                sx={{ color: (theme) => targetValueType === 'percentage' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                %
                                            </ToggleButton>
                                        </Box>
                                    )
                                ) : (
                                    <ToggleButton aria-label="left aligned" selected value="mtm">
                                        MTM
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            </Box>
            <FormHelperText sx={{ minHeight: '20px' }}>
                {targetHelperText}
            </FormHelperText>
        </>
    }, [targetInfoText, selectedSecurity, addTarget, targetValue, toggleAddTarget, onTargetChange, onTargetBlur, preventInputBlur, disabled, isSingleSecurity, targetValueType, onTargetValueTypeChange, disableTarget, isTargetValid, targetInputFocused, targetHovered])


    const stopLossField = useMemo(() => {
        const showSlTooltip = !isSlValid && (slInputFocused || slHovered)
        return <>
            {!disabledCheckbox && <Stack 
                direction="row" 
                alignItems="center" 
                sx={{ marginBottom: 0.5, justifyContent: 'space-between' }}
                onMouseEnter={() => setSlHovered(true)}
                onMouseLeave={() => setSlHovered(false)}
            >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Checkbox checked={addStopLoss} onChange={toggleAddStopLoss} size='small' sx={{ padding: 0, marginRight: 0.5 }} disabled={disabled} />
                    <Typography variant='body1' className={classes.slTargetLabel}>STOP LOSS</Typography>
                </Box>
            </Stack>}
            <Box 
                sx={{ position: 'relative' }}
                onMouseEnter={() => setSlHovered(true)}
                onMouseLeave={() => setSlHovered(false)}
            >
                {showSlTooltip && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.75rem',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            marginBottom: '4px',
                        }}
                    >
                        {slInfoText}
                    </Box>
                )}
                <TextField
                    InputLabelProps={{
                        shrink: true
                    }}
                    type="number"
                    size="small"
                    fullWidth
                    value={slValue}
                    disabled={!addStopLoss || disabled}
                    onChange={onStopLossChange}
                    autoComplete='off'
                    inputRef={stopLossInputRef}
                    error={!isSlValid && addStopLoss}
                    onFocus={() => setSlInputFocused(true)}
                    onBlur={onStopLossBlur}
                    inputProps={{
                        step: 1,
                    }}
                    InputProps={{
                        sx: {
                            '& input[type=number]': {
                                MozAppearance: 'textfield'
                            },
                            paddingRight: 0.5,
                        },
                        endAdornment: (
                            <>
                            {!addStopLoss && !disableStopLoss && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddStopLoss(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={isSingleSecurity ? slValueType : 'mtm'}
                                aria-label="text alignment"
                                onMouseDown={preventInputBlur}
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                {slValue !== '' && (
                                    <ExtraSmallButton
                                        sx={{
                                            minWidth: 28,
                                            height: '28px !important',
                                            p: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        onClick={() => {
                                            dispatch(updateExitStrategy({
                                                addStopLoss: false,
                                                slValue: ''
                                            }))
                                        }}
                                    >
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </ExtraSmallButton>
                                )}
                                {isSingleSecurity ? (
                                    slValueType !== 'exact' && (
                                        <Box component="span" sx={{ display: 'contents' }}>
                                            <ToggleButton
                                                value="points"
                                                onClick={onSlValueTypeChange}
                                                sx={{ color: (theme) => slValueType === 'points' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                Pts
                                            </ToggleButton>
                                            <ToggleButton
                                                value="percentage"
                                                onClick={onSlValueTypeChange}
                                                sx={{ color: (theme) => slValueType === 'percentage' ? theme.palette.text.primary : theme.palette.text.secondary }}
                                            >
                                                %
                                            </ToggleButton>
                                        </Box>
                                    )
                                ) : (
                                    <ToggleButton aria-label="left aligned" selected value="mtm">
                                        MTM
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            </Box>
            <FormHelperText sx={{ minHeight: '20px' }}>
                {slHelperText}
            </FormHelperText>
        </>
    }, [slInfoText, selectedSecurity, addStopLoss, slValue, toggleAddStopLoss, onStopLossChange, onStopLossBlur, preventInputBlur, disabled, isSingleSecurity, slValueType, onSlValueTypeChange, disableStopLoss, isSlValid, slInputFocused, slHovered])

    return (
        <Grid container gap={2} className={disabled ? classes.disabled : undefined} sx={{ paddingTop: 1 }} >
            <Grid container item xs={12} spacing={2}>
                {(!modifyType || modifyType === ModifyType.STOPLOSS) && <Grid item xs={6} paddingLeft={0} >
                    {stopLossField}
                </Grid>}
                {(!modifyType || modifyType === ModifyType.TARGET) && <Grid item xs={6} paddingLeft={0} >
                    {targetField}
                </Grid>}
            </Grid>
        </Grid>
    )
}

export default memo(SlAndTargetInputs)